/* eslint-disable no-console, no-underscore-dangle */
import _ from 'lodash';
import { Base64 } from 'js-base64';

import locales from './mockData/locales.json';

const getRuntimeConfig = () => {
  if (_.isEmpty(window.__runtime_environment)) {
    if (_.isEmpty(process.env.REACT_APP_SERIALIZED_ENV)) {
      return {}; // Dev mode. Will get env values in the .env file
    }
  }

  const base64EncodedRuntime =
    window.__runtime_environment ?? process.env.REACT_APP_SERIALIZED_ENV;

  try {
    const jsonEncodedRuntime = Base64.decode(base64EncodedRuntime);

    if (!_.isEmpty(jsonEncodedRuntime)) {
      const decodedConfig = JSON.parse(jsonEncodedRuntime);
      if (!_.isNil(decodedConfig)) {
        return decodedConfig;
      }
    }

    return {};
  } catch (error) {
    return {};
  }
};

const readConfigVar = (varName, defaultValue) => {
  const runtimeConfig = getRuntimeConfig();
  const hasConfigValue = (value) =>
    !_.isNil(value) &&
    (_.isNumber(value) || !_.isEmpty(value) || _.isArray(value));

  if (hasConfigValue(runtimeConfig[varName])) {
    return runtimeConfig[varName];
  }
  if (hasConfigValue(process.env[varName])) {
    return process.env[varName];
  }

  if (!_.isNil(defaultValue)) {
    return defaultValue;
  }

  throw new Error(`Config key ${varName} not found`);
};

// Possible values: local, dev, staging, production
const getEnvType = () => {
  const forcedEnv = readConfigVar('REACT_APP_FORCE_ENV', 'unknown');
  if (forcedEnv && forcedEnv !== 'unknown') {
    return forcedEnv;
  }

  return readConfigVar('REACT_APP_EATZY_ENVIRONMENT');
};

const deriveAuthHubUrl = (apiUrl) => {
  try {
    const url = new URL(apiUrl);
    const tld = url.hostname.endsWith('.com') ? 'com' : 'net';
    const domain = tld === 'com' ? 'eatzy' : 'eatsy';
    return `https://eatzy-auth.intern.${domain}.${tld}`;
  } catch (e) {
    return 'https://eatzy-auth.intern.eatsy.net';
  }
};

export const createConfig = () => {
  const consumerSetup =
    readConfigVar('REACT_APP_CONFIG_SCOPE', 'business') === 'business'
      ? JSON.parse(readConfigVar('REACT_APP_CONSUMER_SETUP'))
      : null;
  const reactAppImages = readConfigVar('REACT_APP_IMAGES', []);
  const reactAppLocales = readConfigVar('REACT_APP_LOCALES', []);
  const reactAppDomains =
    readConfigVar('REACT_APP_DOMAINS', '') !== ''
      ? JSON.parse(readConfigVar('REACT_APP_DOMAINS'))
      : null;

  const envImages =
    typeof reactAppImages === 'string'
      ? JSON.parse(reactAppImages)
      : reactAppImages;

  const envLocales =
    typeof reactAppLocales === 'string'
      ? JSON.parse(reactAppLocales)
      : reactAppLocales;
  // An explicit null in a site's runtime config must not fall back to a
  // possibly unrelated ID compiled into the shared frontend bundle.
  const runtimeConfig = getRuntimeConfig();
  const configuredRestaurantId = Object.prototype.hasOwnProperty.call(
    runtimeConfig,
    'REACT_APP_RESTAURANT_ID'
  )
    ? runtimeConfig.REACT_APP_RESTAURANT_ID
    : readConfigVar('REACT_APP_RESTAURANT_ID', '');
  const hasNumericRestaurantIdType =
    typeof configuredRestaurantId === 'number' ||
    typeof configuredRestaurantId === 'string';
  const restaurantId =
    !hasNumericRestaurantIdType || configuredRestaurantId.trim?.() === ''
      ? null
      : Number(configuredRestaurantId);

  return {
    apiBaseUrl: readConfigVar('REACT_APP_API_URL'),
    profileKey: readConfigVar('REACT_APP_PROFILE_KEY'),
    restaurantId:
      Number.isSafeInteger(restaurantId) && restaurantId > 0
        ? restaurantId
        : null,
    bugsnagKey: readConfigVar('REACT_APP_BUGSNAG_API_KEY'),
    gaTrackingId: readConfigVar('REACT_APP_GA_TRACKING_ID'),
    zohoSalesIQTrackingId: readConfigVar(
      'REACT_APP_EATZY_ZOHO_SALESIQ_TRACKING_ID'
    ),
    keys: {
      map: readConfigVar('REACT_APP_MAP_KEY'),
      stripe: readConfigVar('REACT_APP_STRIPE_KEY'),
      googleAutocomplete: readConfigVar('REACT_APP_GOOGLE_AUTOCOMPLETE_KEY'),
    },
    timezone: readConfigVar('REACT_APP_BUSINESS_TIMEZONE'),
    googleMapApiUrl: 'https://maps.googleapis.com',
    images: envImages,
    locales: envLocales,
    domains: reactAppDomains,
    textsPlaceholder: locales,
    version: readConfigVar('REACT_APP_VERSION', '0.0.0'),
    runtimeProvider: readConfigVar('REACT_APP_RUNTIME_PROVIDER', 'lr'),
    useFakeData: readConfigVar('REACT_APP_API_FAKE_MODE', 'false'),
    consumerSetup,
    zohoCode: readConfigVar('REACT_APP_ZOHO_CODE', ''),
    appType: readConfigVar('REACT_APP_CONFIG_SCOPE', 'business'),
    authHubUrl: readConfigVar(
      'REACT_APP_AUTH_HUB_URL',
      deriveAuthHubUrl(readConfigVar('REACT_APP_API_URL'))
    ),
  };
};

export const getConfig = () => {
  let appConfig;
  if (_.isEmpty(window.__cached_app_config)) {
    appConfig = createConfig();
    if (appConfig) {
      window.__cached_app_config = appConfig;
    }
  } else {
    appConfig = window.__cached_app_config;
  }

  return appConfig;
};

export const envTypes = {
  production: 'production',
  staging: 'staging',
  local: 'local',
  test: 'test',
  dev: 'development',
};

/**
 * Check env type
 * @param {string | string[]} env one of envTypes or array of envTypes values
 */
export const isEnvMatch = (env) => {
  const envType = getEnvType();
  const hasMultipleEnvs = envType.split(',').length > 1;
  if (hasMultipleEnvs) {
    return envType.includes(env);
  }

  return envType === env;
};

export default getConfig;
